import { combineReducers, Action, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LubavSessionType } from '@backrs_ui/grow-ui/src/models/models';

import { boxDataReducer } from '../boxData/boxData';
import { coachLubavReducer } from '../coachLubav/coachLubav';
import { logout } from '../session/logoutThunk';
import { solutionSessionReducer } from '../solutionSession/solutionSession';

// combined reducer for session-specific state
const sessionSpecificReducer = combineReducers({
  coachLubav: coachLubavReducer,
  boxData: boxDataReducer,
  solutionSession: solutionSessionReducer,
});

export type SessionStateMap = Record<LubavSessionType, ReturnType<typeof sessionSpecificReducer>>;

type SessionState = Readonly<{
  activeSessionType: LubavSessionType | null;
  sessions: SessionStateMap;
}>;

const initialState: SessionState = {
  activeSessionType: null,
  sessions: {},
};

const sessionSlice = createSlice({
  name: 'lubavSession',
  initialState,
  reducers: {
    setActiveSessionType: (state, action: PayloadAction<LubavSessionType | null>) => {
      state.activeSessionType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // LOGOUT
      .addCase(logout, (state) => {
        state.activeSessionType = null;
        state.sessions = {};
      });
  },
});

export const { setActiveSessionType } = sessionSlice.actions;

export const sessionStateReducer = (
  state: SessionState = initialState,
  action: Action &
    Partial<{
      sessionType: LubavSessionType;
      meta?: { arg?: { activeSession?: LubavSessionType } };
    }>,
): SessionState => {
  const sliceState = sessionSlice.reducer(state, action);

  if (sliceState !== state) {
    return sliceState;
  }

  // get session type from either direct property (for actions) or metadata (for thunks)
  const sessionType = action.sessionType || action.meta?.arg?.activeSession;

  if (!sessionType) return state;

  const newSessionState = sessionSpecificReducer(state.sessions[sessionType], {
    ...action,
    sessionType,
  });

  if (state.sessions[sessionType] === newSessionState) {
    return state;
  }

  return {
    ...state,
    sessions: { ...state.sessions, [sessionType]: newSessionState },
  };
};
